/**
 * WithdrawAcceptanceDialog
 *
 * Internal staff-only confirm dialog wired to POST /offers/:id/versions/:versionId/reject
 * (useRejectVersion). Two modes, both rendered on the Versions tab:
 * - 'withdraw' — target version is ACCEPTED. Shows a server-driven pre-commit
 *   breakdown of the reservations locked for this version before any request
 *   is issued, and on success replays that breakdown as the released/failed
 *   outcome result (task 4.3 — see withdrawal-breakdown-view.tsx).
 * - 'registerRejection' — target version is PUBLISHED and was never accepted.
 *   No reservations are affected; a plain confirm + success state.
 *
 * offer-versioning-diff / Internal acceptance-withdrawal control on the Versions tab
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { offerVersionEndpoints, parseApiError } from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { useSubmitGuard } from '@ooh/ui/lib/use-submit-guard';
import type { RejectVersionResponse, WithdrawalPreviewResponse } from '@ooh/contracts';
import { useApiQuery } from '../../data/use-api-query';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';
import type { OutcomeResultUnitLookup } from '../../reservations/components/outcome-result-buckets';
import { useRejectVersion } from './api/mutations';
import { useWithdrawalPreview } from './api/previews';
import { unitDecisionsRequest } from './api/queries';
import type { WithdrawAcceptanceMode } from './version-row-actions';
import { WithdrawReasonField } from './withdraw-reason-field';
import {
  WithdrawalBreakdownView,
  WithdrawalResultView,
  type WithdrawalBreakdownPreview,
} from './withdrawal-breakdown-view';

const DIALOG_MAX_WIDTH = 'max-w-lg';

export interface WithdrawAcceptanceDialogProps {
  open: boolean;
  onClose: () => void;
  offerId: string;
  /** null when no version is targeted yet — dialog renders nothing. */
  versionId: number | null;
  mode: WithdrawAcceptanceMode;
}

/** Resolves the mode-dependent title/description pair — extracted to keep
 * WithdrawAcceptanceDialog's cyclomatic complexity within the lint budget.
 * The 'withdraw' description is a fixed statement, not a count: the
 * pre-commit breakdown (rendered below it) names each affected nośnik, so a
 * bare count no longer stands in for the listing (offer-versioning-diff /
 * "A bare count SHALL NOT stand in for the listing"). */
function useDialogCopy(mode: WithdrawAcceptanceMode) {
  const { t } = useTranslation('offers');
  if (mode === 'withdraw') {
    return {
      title: t('workspace.versions.withdrawAcceptance.dialog.withdrawTitle'),
      description: t('workspace.versions.withdrawAcceptance.dialog.withdrawDescription'),
    };
  }
  return {
    title: t('workspace.versions.withdrawAcceptance.dialog.registerRejectionTitle'),
    description: t('workspace.versions.withdrawAcceptance.dialog.registerRejectionDescription'),
  };
}

function ConfirmStep({
  description,
  isSuperseded,
  reason,
  onReasonChange,
  breakdown,
}: {
  description: string;
  isSuperseded: boolean;
  reason: string;
  onReasonChange: (value: string) => void;
  /** Only 'withdraw' mode has a breakdown — undefined in 'registerRejection'. */
  breakdown?: React.ReactNode;
}) {
  const { t } = useTranslation('offers');
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground" data-testid="withdraw-acceptance-description">
        {description}
      </p>
      {isSuperseded ? (
        <p className="text-sm text-destructive" data-testid="withdraw-acceptance-superseded">
          {t('workspace.versions.withdrawAcceptance.dialog.supersededMessage')}
        </p>
      ) : null}
      {breakdown}
      <WithdrawReasonField
        id="withdraw-acceptance-reason"
        testId="withdraw-acceptance-reason-input"
        label={t('workspace.versions.withdrawAcceptance.dialog.reasonLabel')}
        value={reason}
        onChange={onReasonChange}
      />
    </div>
  );
}

/**
 * Submits the withdrawal/rejection and reports the outcome. Extracted to a
 * standalone function (rather than an inline closure) to keep
 * WithdrawAcceptanceDialog's cyclomatic complexity within the lint budget.
 * 409 (superseded) is left for the caller to surface inline via mutation
 * error state — every other failure gets the standard toast here.
 */
async function submitWithdrawal(
  withdrawMutation: ReturnType<typeof useRejectVersion>,
  vars: { offerId: string; versionId: number; reason: string },
): Promise<RejectVersionResponse | null> {
  try {
    return await withdrawMutation.mutateAsync({
      offerId: vars.offerId,
      versionId: vars.versionId,
      reason: vars.reason.trim() || undefined,
    });
  } catch (error) {
    if (parseApiError(error).statusCode !== 409) {
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation'));
    }
    return null;
  }
}

/** The confirm control waits for the breakdown, in 'withdraw' mode only
 * (action-breakdown / "The confirm control SHALL remain unavailable until
 * the breakdown has resolved"). 'registerRejection' has no breakdown to
 * wait for. */
function isConfirmDisabled(
  mode: WithdrawAcceptanceMode,
  isPending: boolean,
  breakdownIsLoading: boolean,
  breakdownIsError: boolean,
): boolean {
  if (isPending) return true;
  return mode === 'withdraw' && (breakdownIsLoading || breakdownIsError);
}

/** Only 'withdraw' mode has a breakdown to show — extracted out of the JSX
 * ternary to keep WithdrawAcceptanceDialog's own cyclomatic complexity
 * within the lint budget. */
function renderBreakdown(
  mode: WithdrawAcceptanceMode,
  preview: WithdrawalBreakdownPreview,
  lookup: OutcomeResultUnitLookup,
): React.ReactNode {
  if (mode !== 'withdraw') return undefined;
  return <WithdrawalBreakdownView preview={preview} lookup={lookup} />;
}

/** Result step body — only the 'withdraw' mode has a reservation-release
 * outcome to show, replayed from the confirmed breakdown snapshot (see
 * withdrawal-breakdown-view.tsx's own doc comment for why). */
function ResultStep({
  mode,
  committedItems,
  lookup,
}: {
  mode: WithdrawAcceptanceMode;
  committedItems: WithdrawalPreviewResponse;
  lookup: OutcomeResultUnitLookup;
}) {
  if (mode !== 'withdraw') return null;
  return <WithdrawalResultView committedItems={committedItems} lookup={lookup} />;
}

export function WithdrawAcceptanceDialog({
  open,
  onClose,
  offerId,
  versionId,
  mode,
}: WithdrawAcceptanceDialogProps) {
  const { t } = useTranslation('offers');
  const [reason, setReason] = React.useState('');
  const [result, setResult] = React.useState<RejectVersionResponse | null>(null);
  const [committedItems, setCommittedItems] = React.useState<WithdrawalPreviewResponse>([]);
  const withdrawMutation = useRejectVersion();
  const submitGuard = useSubmitGuard();
  const isWithdrawTargetActive = open && mode === 'withdraw' && versionId != null;
  // Resolves display metadata (GRW id, address, city) for the breakdown/result
  // rows — same query LockReservationsDialog already runs for its own rows.
  const decisionsQuery = useApiQuery(
    offerVersionEndpoints.unitDecisions,
    unitDecisionsRequest(offerId, versionId ?? 0),
    { enabled: isWithdrawTargetActive },
  );
  const unitLookup = React.useMemo<OutcomeResultUnitLookup>(() => {
    const map: OutcomeResultUnitLookup = new Map();
    for (const item of decisionsQuery.data ?? []) {
      map.set(item.mediaUnitId, item);
    }
    return map;
  }, [decisionsQuery.data]);
  // Pre-commit breakdown (offer-versioning-diff / "Acceptance-withdrawal
  // preview") — only fetched in 'withdraw' mode; 'registerRejection' affects
  // no reservation.
  const preview = useWithdrawalPreview(offerId, versionId ?? 0, {
    enabled: isWithdrawTargetActive,
  });
  // Called unconditionally (before the early return below) — it wraps
  // useTranslation, so it must obey the Rules of Hooks like every other hook here.
  const { title, description } = useDialogCopy(mode);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setResult(null);
      setCommittedItems([]);
      withdrawMutation.reset();
    }
    // withdrawMutation identity is stable across renders (useMutation) — including it
    // would re-run this effect on every mutate() call and reset state mid-flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || versionId == null) return null;
  // TS does not narrow `versionId` across a nested `function` declaration —
  // capture the narrowed value in a new const for handleConfirm to close over.
  const activeVersionId = versionId;

  const isSuperseded =
    withdrawMutation.isError && parseApiError(withdrawMutation.error).statusCode === 409;

  async function handleConfirm() {
    // Synchronous same-click guard, in addition to the async isPending check
    // above — see use-submit-guard.ts's doc comment (breakdown-dry-fixes fix #4).
    if (withdrawMutation.isPending || !submitGuard.tryStart()) return;
    try {
      // Snapshot the just-shown breakdown before the request is sent, so the
      // result view names the same nośniki the caller already saw — see
      // withdrawal-breakdown-view.tsx's doc comment for why this replaces
      // reading the withdrawal response's own reservationRelease.
      const snapshot = preview.data ?? [];
      const response = await submitWithdrawal(withdrawMutation, {
        offerId,
        versionId: activeVersionId,
        reason,
      });
      if (response) {
        setResult(response);
        setCommittedItems(snapshot);
      }
    } finally {
      submitGuard.finish();
    }
  }

  const confirmDisabled = isConfirmDisabled(
    mode,
    withdrawMutation.isPending,
    preview.isLoading,
    preview.isError,
  );

  return (
    <ModalDialog
      onClose={onClose}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="withdraw-acceptance-dialog"
      header={<h2 className="text-base font-semibold">{title}</h2>}
      footer={
        result == null ? (
          <>
            <Button variant="outline" onClick={onClose}>
              {t('workspace.versions.withdrawAcceptance.dialog.cancelBtn')}
            </Button>
            <Button
              disabled={confirmDisabled}
              onClick={() => void handleConfirm()}
              data-testid="withdraw-acceptance-confirm-button"
            >
              {t('workspace.versions.withdrawAcceptance.dialog.confirmBtn')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>
            {t('workspace.versions.withdrawAcceptance.result.closeBtn')}
          </Button>
        )
      }
    >
      {result == null ? (
        <ConfirmStep
          description={description}
          isSuperseded={isSuperseded}
          reason={reason}
          onReasonChange={setReason}
          breakdown={renderBreakdown(mode, preview, unitLookup)}
        />
      ) : (
        <ResultStep mode={mode} committedItems={committedItems} lookup={unitLookup} />
      )}
    </ModalDialog>
  );
}
